import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { popupConfig, shouldShowPopup, markPopupAsShown } from '../config/popup';

const MultiPurposePopup = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Verificar si el popup debe mostrarse
    if (shouldShowPopup()) {
      // Pequeño delay para que la página cargue completamente
      const timer = setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
        markPopupAsShown();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    // Auto-cerrar si está configurado
    if (isVisible && popupConfig.autoClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, popupConfig.autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  const handleActionClick = () => {
    if (popupConfig.actionButton.external) {
      window.open(popupConfig.actionButton.link, '_blank');
    } else {
      window.location.href = popupConfig.actionButton.link;
    }
    handleClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible) return null;

  // Clases de tamaño
  const sizeClasses = {
    small: 'max-w-md',
    medium: 'max-w-lg',
    large: 'max-w-2xl'
  };

  // Clases de tema
  const themeClasses = {
    light: {
      bg: 'bg-white',
      text: 'text-gray-900',
      textSecondary: 'text-gray-600',
      border: 'border-gray-200'
    },
    dark: {
      bg: 'bg-gray-900',
      text: 'text-white',
      textSecondary: 'text-gray-300',
      border: 'border-gray-700'
    }
  };

  const currentTheme = themeClasses[popupConfig.theme] || themeClasses.light;
  const currentSize = sizeClasses[popupConfig.size] || sizeClasses.medium;
  const popupTitle = t('startup_popup.title', { defaultValue: popupConfig.title });
  const popupContent = t('startup_popup.content', { defaultValue: popupConfig.content });
  const popupClose = t('startup_popup.close', { defaultValue: 'Cerrar' });
  const popupActionText = t('startup_popup.action', { defaultValue: popupConfig.actionButton.text });

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black transition-opacity duration-300 ${
        isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
      }`}
      onClick={handleOverlayClick}
    >
      <div 
        className={`relative w-full ${currentSize} ${currentTheme.bg} rounded-2xl shadow-2xl border ${currentTheme.border} transform transition-all duration-300 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Botón de cerrar */}
        <button
          onClick={handleClose}
          className={`absolute top-4 right-4 p-2 rounded-full transition-colors z-10 ${
            popupConfig.theme === 'dark' 
              ? 'text-white hover:bg-gray-800 hover:text-white' 
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }`}
        >
          <X size={20} />
        </button>

        <div className="p-6 sm:p-8">
          {/* Media (Imagen o Video) */}
          {popupConfig.mediaType !== 'none' && popupConfig.mediaUrl && (
            <div className="mb-6">
              {popupConfig.mediaType === 'image' ? (
                <img
                  src={popupConfig.mediaUrl}
                  alt="Popup media"
                  className="w-full h-48 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : popupConfig.mediaType === 'video' ? (
                <div className="relative w-full h-48 rounded-lg overflow-hidden">
                  <iframe
                    src={popupConfig.mediaUrl}
                    title="Popup video"
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              ) : null}
            </div>
          )}

          {/* Título */}
          {popupTitle && (
            <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${currentTheme.text}`}>
              {popupTitle}
            </h2>
          )}

          {/* Contenido */}
          {popupContent && (
            <p className={`text-base sm:text-lg leading-relaxed mb-6 ${currentTheme.textSecondary}`}>
              {popupContent}
            </p>
          )}

          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={handleClose}
              className={`px-6 py-2 rounded-lg border transition-colors ${
                popupConfig.theme === 'dark'
                  ? 'border-gray-700 text-white hover:bg-gray-800 hover:text-white'
                  : 'border-gray-200 text-gray-900 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              {popupClose}
            </button>
            
            {popupConfig.actionButton.enabled && (
              <button
                onClick={handleActionClick}
                className="px-6 py-2 bg-[#141414] text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
              >
                {popupActionText}
                {popupConfig.actionButton.external && <ExternalLink size={16} />}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiPurposePopup;
